import React, { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { UploadSimple, Trash, FileText, ImageSquare, Eye, Plus } from "@phosphor-icons/react";
import { logger } from "@/lib/logger";

const ALLOWED = ["jpg", "jpeg", "png", "gif", "webp", "pdf", "txt", "doc", "docx"];

export default function EvidenceVault({ caseId }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState(null);
  const inputRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/cases/${caseId}/evidence`);
      setFiles(data);
    } catch (err) { logger.error("evidence list fetch failed", err); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [caseId]);

  const upload = async (file) => {
    const ext = file.name.split(".").pop().toLowerCase();
    if (!ALLOWED.includes(ext)) {
      toast.error(`.${ext} not allowed`);
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File exceeds 10MB");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/cases/${caseId}/evidence`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Evidence locked into vault.");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (f) upload(f);
    e.target.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) upload(f);
  };

  const remove = async (id) => {
    if (!confirm("Remove this evidence?")) return;
    try {
      await api.delete(`/evidence/${id}`);
      toast.success("Evidence removed.");
      setFiles((f) => f.filter((x) => x.id !== id));
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const previewUrl = (ev) => {
    // Prefer pre-signed URL from API (no JWT leakage); fall back to JWT query param
    if (ev.signed_url) return `${API.replace(/\/api$/, "")}${ev.signed_url}`;
    const token = localStorage.getItem("sf_token");
    return `${API}/evidence/${ev.id}/download?auth=${token}`;
  };

  const isImage = (ct) => ct?.startsWith("image/");

  return (
    <div className="border border-[#1A1A1A] p-6 md:p-8" data-testid="evidence-vault">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <div className="font-mono-sf text-[10px] uppercase tracking-[0.3em] text-[#FF3366]">// vault</div>
          <h2 className="font-heading font-bold uppercase tracking-tight text-2xl mt-1">Evidence Vault</h2>
        </div>
        <span className="font-mono-sf text-[10px] uppercase tracking-[0.25em] text-neutral-500">{files.length} sealed</span>
      </div>

      {/* Drag-drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`cursor-pointer border-2 border-dashed transition-colors p-8 text-center ${dragOver ? "border-[#FF3366] bg-[#FF3366]/5" : "border-[#1A1A1A] hover:border-[#FF3366]/60 bg-[#0D0D0D]"}`}
        data-testid="evidence-dropzone"
      >
        <UploadSimple size={32} weight="bold" className={`mx-auto mb-3 ${dragOver ? "text-[#FF3366]" : "text-neutral-500"}`} />
        <div className="font-heading font-bold uppercase tracking-tight text-base mb-1">
          {uploading ? "Encrypting & locking..." : "Drop screenshot or document"}
        </div>
        <div className="font-mono-sf text-[10px] uppercase tracking-[0.25em] text-neutral-500">
          jpg / png / pdf / doc / txt &middot; max 10MB &middot; encrypted at rest
        </div>
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.txt,.doc,.docx"
          onChange={onPick}
          data-testid="evidence-file-input"
        />
      </div>

      {/* File list */}
      {files.length > 0 && (
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#1A1A1A] border border-[#1A1A1A]" data-testid="evidence-list">
          {files.map((ev) => {
            const img = isImage(ev.content_type);
            return (
              <div key={ev.id} className="bg-[#0D0D0D] p-4 flex flex-col gap-3" data-testid={`evidence-${ev.id}`}>
                <div className="aspect-video bg-[#1A1A1A] border border-[#1A1A1A] flex items-center justify-center overflow-hidden">
                  {img ? (
                    <img
                      src={previewUrl(ev)}
                      alt={ev.original_filename}
                      className="w-full h-full object-cover"
                      onClick={() => setPreview(ev)}
                      style={{ cursor: "zoom-in" }}
                    />
                  ) : (
                    <FileText size={36} weight="bold" className="text-neutral-600" />
                  )}
                </div>
                <div className="min-w-0">
                  <div className="font-mono-sf text-xs truncate text-white" title={ev.original_filename}>{ev.original_filename}</div>
                  <div className="font-mono-sf text-[10px] uppercase tracking-wider text-neutral-500 mt-1">
                    {(ev.size / 1024).toFixed(1)}KB &middot; {new Date(ev.uploaded_at).toLocaleDateString()}
                  </div>
                </div>
                <div className="flex gap-2 mt-auto">
                  <a
                    href={previewUrl(ev)}
                    target="_blank"
                    rel="noreferrer"
                    className="flex-1 h-8 border border-[#1A1A1A] hover:border-[#00FFCC] hover:text-[#00FFCC] font-mono-sf text-[10px] uppercase tracking-[0.2em] flex items-center justify-center gap-2 transition-colors"
                    data-testid={`evidence-view-${ev.id}`}
                  >
                    <Eye size={12} weight="bold" /> View
                  </a>
                  <button
                    onClick={() => remove(ev.id)}
                    className="h-8 w-8 border border-[#1A1A1A] hover:border-[#FF3366] hover:text-[#FF3366] flex items-center justify-center transition-colors"
                    data-testid={`evidence-delete-${ev.id}`}
                    title="Remove"
                  >
                    <Trash size={12} weight="bold" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Lightbox */}
      {preview && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-6"
          onClick={() => setPreview(null)}
          data-testid="evidence-lightbox"
        >
          <img src={previewUrl(preview)} alt={preview.original_filename} className="max-w-full max-h-full" />
        </div>
      )}
    </div>
  );
}
